import React, { useState, useRef, useEffect } from 'react';
import { Calendar, DollarSign, AlertTriangle, Link, User, Tag, ChevronRight } from 'lucide-react';

/**
 * Unified Hover Tooltip Component
 * Implements Section 8.1 of the ScopeOps PRD
 * 
 * Provides consistent tooltip schema for ALL hierarchy levels:
 * Portfolio, Program, Project, Epic, Feature, Story, Task, Milestone
 */

const CURRENCY_SYMBOLS = { ZAR: 'R', USD: '$', EUR: '€', GBP: '£' };

const formatCurrency = (amount, currency = 'ZAR') => {
  if (amount === null || amount === undefined) return null;
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatDate = (dateStr, scale = 'month') => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (scale === 'quarter') {
    const q = Math.ceil((date.getMonth() + 1) / 3);
    return `${date.getFullYear()}-Q${q}`;
  }
  return date.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short' });
};

const getStatusColor = (status) => {
  const colors = {
    'blocked': { bg: 'bg-[var(--clr-sev-blocked)]', text: 'text-[var(--clr-sev-blocked)]', light: 'bg-[var(--clr-sev-blocked-bg)]' },
    'behind': { bg: 'bg-[var(--clr-sev-behind)]', text: 'text-[var(--clr-sev-behind)]', light: 'bg-[var(--clr-sev-behind-bg)]' },
    'due-at-risk': { bg: 'bg-[var(--clr-sev-atrisk)]', text: 'text-[var(--clr-sev-atrisk)]', light: 'bg-[var(--clr-sev-atrisk-bg)]' },
    'in-progress': { bg: 'bg-[var(--clr-sev-inprogress)]', text: 'text-[var(--clr-sev-inprogress)]', light: 'bg-[var(--clr-sev-inprogress-bg)]' },
    'in-review': { bg: 'bg-[var(--clr-brand-primary)]', text: 'text-[var(--clr-brand-primary)]', light: 'bg-[var(--clr-brand-primary)] bg-opacity-10' },
    'not-started': { bg: 'bg-[var(--clr-sev-notstarted)]', text: 'text-[var(--clr-text-secondary)]', light: 'bg-[var(--clr-surface-subtle)]' },
    'completed': { bg: 'bg-[var(--clr-sev-completed)]', text: 'text-[var(--clr-sev-completed)]', light: 'bg-[var(--clr-sev-completed-bg)]' }
  };
  const key = status?.toLowerCase().replace(/\s+/g, '-');
  return colors[key] || colors['in-progress'];
};

const getTypeColor = (type) => {
  const colors = {
    'portfolio': 'bg-[var(--clr-brand-primary)]',
    'program': 'bg-[var(--clr-brand-secondary)]',
    'project': 'bg-[var(--clr-sev-inprogress)]',
    'epic': 'bg-[var(--clr-brand-primary)]',
    'feature': 'bg-[var(--clr-sev-healthy)]',
    'story': 'bg-[var(--clr-sev-inprogress)]',
    'task': 'bg-[var(--clr-sev-notstarted)]',
    'milestone': 'bg-[var(--clr-sev-atrisk)]'
  };
  return colors[type?.toLowerCase()] || 'bg-[var(--clr-sev-notstarted)]';
};

/**
 * UnifiedTooltip Component
 * 
 * Props:
 * - data: Object matching the unified tooltip schema
 * - position: 'top' | 'bottom' | 'left' | 'right'
 * - visible: boolean
 * - onClose: function
 * - onClick: function (for "Click for details")
 * - currency: string (tenant currency, default 'ZAR')
 * - dateScale: 'month' | 'quarter'
 */
const UnifiedTooltip = ({ 
  data, 
  position = 'top', 
  visible = false,
  onClose,
  onClick,
  currency = 'ZAR',
  dateScale = 'month'
}) => {
  if (!visible || !data) return null;

  const statusColors = getStatusColor(data.status);
  const typeColor = getTypeColor(data.type);

  // Position classes
  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };

  return (
    <div 
      className={`absolute z-50 ${positionClasses[position]} animate-in fade-in duration-150`}
      role="tooltip"
      aria-live="polite"
    >
      <div className="bg-[var(--clr-surface-card)] rounded-lg shadow-xl border border-gray-200 w-[340px] max-w-[90vw] overflow-hidden">
        {/* Header */}
        <div className="p-3 border-b bg-[var(--clr-surface-subtle)]">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              {/* Type Badge */}
              <span className={`inline-block px-2 py-0.5 text-xs font-medium text-white rounded ${typeColor} mb-1`}>
                {data.type}
              </span>
              {/* Name */}
              <h3 className="font-semibold text-[var(--clr-text-primary)] text-sm leading-tight truncate" title={data.name}>
                {data.name?.length > 50 ? data.name.slice(0, 50) + '...' : data.name}
              </h3>
            </div>
            {/* Status Chip */}
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${statusColors.light} ${statusColors.text}`}>
              {data.status}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-3 space-y-3">
          {/* Progress */}
          {data.percent_complete !== undefined && (
            <div>
              <div className="flex items-center justify-between text-xs text-[var(--clr-text-secondary)] mb-1">
                <span>Progress</span>
                <span className="font-medium">{data.percent_complete}%</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${statusColors.bg}`}
                  style={{ width: `${data.percent_complete}%` }}
                />
              </div>
            </div>
          )}

          {/* Dates */}
          {data.dates && (
            <div className="flex items-center gap-2 text-xs">
              <Calendar className="w-3.5 h-3.5 text-[var(--clr-text-muted)] flex-shrink-0" />
              <div className="flex items-center gap-1 flex-wrap">
                {data.dates.start && (
                  <span className="text-[var(--clr-text-secondary)]">{formatDate(data.dates.start, dateScale)}</span>
                )}
                {data.dates.start && data.dates.due && <span className="text-[var(--clr-text-muted)]">→</span>}
                {data.dates.due && (
                  <span className="text-[var(--clr-text-secondary)]">{formatDate(data.dates.due, dateScale)}</span>
                )}
                {data.dates.revised && data.dates.revised !== data.dates.due && (
                  <span className="text-[var(--clr-sev-blocked)] ml-1">
                    (Revised: {formatDate(data.dates.revised, dateScale)})
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Cost */}
          {data.cost && (data.cost.est || data.cost.act) && (
            <div className="flex items-center gap-2 text-xs">
              <DollarSign className="w-3.5 h-3.5 text-[var(--clr-text-muted)] flex-shrink-0" />
              <div className="flex items-center gap-2">
                {data.cost.est && (
                  <span className="text-[var(--clr-text-secondary)]">
                    Est: <span className="font-medium">{formatCurrency(data.cost.est, data.cost.currency || currency)}</span>
                  </span>
                )}
                {data.cost.act && (
                  <span className="text-[var(--clr-text-secondary)]">
                    Act: <span className="font-medium">{formatCurrency(data.cost.act, data.cost.currency || currency)}</span>
                  </span>
                )}
                {data.cost.var !== undefined && data.cost.var !== 0 && (
                  <span className={`font-medium ${data.cost.var > 0 ? 'text-[var(--clr-sev-blocked)]' : 'text-[var(--clr-sev-healthy)]'}`}>
                    ({data.cost.var > 0 ? '+' : ''}{formatCurrency(data.cost.var, data.cost.currency || currency)})
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Counts (Issues/Dependencies) */}
          {data.counts && (data.counts.issues > 0 || data.counts.dependencies > 0) && (
            <div className="flex items-center gap-3 text-xs">
              {data.counts.issues > 0 && (
                <div className="flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5 text-[var(--clr-sev-atrisk)]" />
                  <span className="text-[var(--clr-text-secondary)]">{data.counts.issues} issue{data.counts.issues !== 1 ? 's' : ''}</span>
                </div>
              )}
              {data.counts.dependencies > 0 && (
                <div className="flex items-center gap-1">
                  <Link className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-[var(--clr-text-secondary)]">{data.counts.dependencies} dep{data.counts.dependencies !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          )}

          {/* Owner */}
          {data.owner && (
            <div className="flex items-center gap-2 text-xs">
              <User className="w-3.5 h-3.5 text-[var(--clr-text-muted)] flex-shrink-0" />
              <span className="text-[var(--clr-text-secondary)]">{data.owner}</span>
            </div>
          )}

          {/* Sprints */}
          {data.sprints && data.sprints.length > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <Tag className="w-3.5 h-3.5 text-[var(--clr-text-muted)] flex-shrink-0" />
              <div className="flex flex-wrap gap-1">
                {data.sprints.length <= 3 ? (
                  data.sprints.map((sprint, i) => (
                    <span key={i} className="px-1.5 py-0.5 bg-[var(--clr-surface-subtle)] text-[var(--clr-text-secondary)] rounded">
                      {sprint}
                    </span>
                  ))
                ) : (
                  <>
                    <span className="px-1.5 py-0.5 bg-[var(--clr-surface-subtle)] text-[var(--clr-text-secondary)] rounded">
                      {data.sprints[0]}
                    </span>
                    <span className="px-1.5 py-0.5 bg-[var(--clr-surface-subtle)] text-[var(--clr-text-secondary)] rounded" title={data.sprints.slice(1).join(', ')}>
                      +{data.sprints.length - 1} more
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Release */}
          {data.release && (
            <div className="flex items-center gap-2 text-xs">
              <Tag className="w-3.5 h-3.5 text-[var(--clr-brand-primary)] flex-shrink-0" />
              <span className="px-1.5 py-0.5 bg-[var(--clr-brand-primary)] bg-opacity-10 text-[var(--clr-brand-primary)] rounded font-medium">
                {data.release}
              </span>
            </div>
          )}
        </div>

        {/* Footer - Click CTA */}
        {onClick && (
          <button
            onClick={onClick}
            className="w-full px-3 py-2 border-t bg-[var(--clr-surface-subtle)] hover:bg-[var(--clr-surface-hover)] transition-colors flex items-center justify-center gap-1 text-xs text-[var(--clr-brand-primary)] font-medium"
          >
            {data.cta || 'Click for details'}
            <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * TooltipWrapper Component
 * Wraps any element with hover/focus tooltip functionality
 */
const TooltipWrapper = ({ 
  children, 
  data, 
  position = 'top',
  onClick,
  currency = 'ZAR',
  dateScale = 'month',
  delay = 300
}) => {
  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  const showTooltip = () => {
    timeoutRef.current = setTimeout(() => setVisible(true), delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setVisible(false);
  };

  // Keyboard support
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setVisible(!visible);
    } else if (e.key === 'Escape') {
      setVisible(false);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div 
      ref={wrapperRef}
      className="relative inline-block"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-haspopup="true"
      aria-expanded={visible}
    >
      {children}
      <UnifiedTooltip
        data={data}
        position={position}
        visible={visible}
        onClose={hideTooltip}
        onClick={onClick}
        currency={currency}
        dateScale={dateScale}
      />
    </div>
  );
};

export { UnifiedTooltip, TooltipWrapper };
export default UnifiedTooltip;
